/**
 * ApplicationGroup Types for MEV-Med Frontend
 * Type definitions for Application Group dashboard (Grupo de Aplicação)
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para visualização consolidada de aplicações
 */

import { ROTULO_DO_NIVEL, type NivelDeAplicacao } from './nivelDeAplicacao';

// ============================================================================
// CONSOLIDATED DATA
// ============================================================================

/**
 * Types of consolidated items
 */
export type ConsolidatedItemType = 'risk' | 'exam' | 'source';

/**
 * Application levels in the organizational hierarchy
 */
export type ApplicationLevel = NivelDeAplicacao;

/**
 * Consolidated application item for the dashboard
 */
export interface ConsolidatedItem {
  type: ConsolidatedItemType;
  id: number;
  name: string;
  code: string | null;
  level: ApplicationLevel;
  targetId: number;
  targetName: string;
  appliedAt: string;
  details: Record<string, unknown>;
}

/**
 * Totals for consolidated data
 */
export interface ConsolidatedTotals {
  risks: number;
  exams: number;
  sources: number;
  total: number;
}

/**
 * Consolidated data for a company
 */
export interface ConsolidatedData {
  companyId: number;
  companyName: string;
  risks: ConsolidatedItem[];
  exams: ConsolidatedItem[];
  sources: ConsolidatedItem[];
  totals: ConsolidatedTotals;
}

// ============================================================================
// API RESPONSE INTERFACES
// ============================================================================

/**
 * Response for consolidated data endpoint
 */
export interface ConsolidatedDataResponse {
  message: string;
  data: {
    companyId: number;
    companyName: string;
    risks: ConsolidatedItem[];
    exams: ConsolidatedItem[];
    sources: ConsolidatedItem[];
  };
  totals: ConsolidatedTotals;
}

/**
 * API error response
 */
export interface ApplicationGroupErrorResponse {
  error: string;
  code: string;
  details?: string;
}

// ============================================================================
// LABELS AND CONSTANTS
// ============================================================================

/**
 * Labels for application levels (Portuguese)
 */
export const ApplicationLevelLabels: Record<ApplicationLevel, string> = ROTULO_DO_NIVEL;

/**
 * Labels for consolidated item types (Portuguese)
 */
export const ConsolidatedItemTypeLabels: Record<ConsolidatedItemType, string> = {
  risk: 'Risco',
  exam: 'Exame',
  source: 'Fonte Geradora',
};

/**
 * Colors for consolidated item types
 */
export const ConsolidatedItemTypeColors: Record<ConsolidatedItemType, 'error' | 'primary' | 'warning'> = {
  risk: 'error',
  exam: 'primary',
  source: 'warning',
};

// ============================================================================
// UI COMPONENT PROPS
// ============================================================================

/**
 * Props for consolidated items table
 */
export interface ConsolidatedTableProps {
  items: ConsolidatedItem[];
  loading?: boolean;
  onItemClick?: (item: ConsolidatedItem) => void;
}

/**
 * Props for summary cards
 */
export interface SummaryCardsProps {
  totals: ConsolidatedTotals;
  loading?: boolean;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get the label for an application level
 */
export function getApplicationLevelLabel(level: ApplicationLevel): string {
  return ApplicationLevelLabels[level] || level;
}

/**
 * Get the label for a consolidated item type
 */
export function getConsolidatedItemTypeLabel(type: ConsolidatedItemType): string {
  return ConsolidatedItemTypeLabels[type] || type;
}

/**
 * Get the color for a consolidated item type
 */
export function getConsolidatedItemTypeColor(type: ConsolidatedItemType): 'error' | 'primary' | 'warning' {
  return ConsolidatedItemTypeColors[type] || 'primary';
}

/**
 * Format date for display
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('pt-BR');
  } catch {
    return dateString;
  }
}
